/**
 * Site Mode Configuration
 *
 * Determines which site/routes should be rendered. Hostname wins in deployed
 * multi-domain environments, with path/env fallback for local development.
 *
 * - healthcarelx: Landing, registration, HLX routes, public pages
 * - medcredvault: Vault routes only
 */
import { useLocation } from 'react-router';
import { detectBrand, detectBrandFromHostname, detectBrandFromPath } from './branding';

export type SiteMode = 'healthcarelx' | 'medcredvault';

/**
 * Detect site mode from URL hostname (multi-domain prod), then path, then env.
 */
export const detectSiteMode = (path?: string): SiteMode => {
  // Hostname wins in multi-domain deployments — one bundle on two domains.
  const hostBrand = detectBrandFromHostname();
  if (hostBrand) return hostBrand === 'mcv' ? 'medcredvault' : 'healthcarelx';

  const pathname = path ?? window.location.pathname;
  const pathBrand = detectBrandFromPath(pathname);
  if (pathBrand) return pathBrand === 'mcv' ? 'medcredvault' : 'healthcarelx';

  // Env var fallback for local dev / tests (only when no URL path matched above)
  const envSiteMode = import.meta.env.VITE_SITE_MODE as SiteMode;
  if (envSiteMode === 'healthcarelx' || envSiteMode === 'medcredvault') {
    return envSiteMode;
  }

  if (path === undefined) {
    return detectBrand() === 'mcv' ? 'medcredvault' : 'healthcarelx';
  }

  return 'healthcarelx';
};

/**
 * Hook to get current site mode
 */
export const useSiteMode = (): SiteMode => {
  const { pathname } = useLocation();
  return detectSiteMode(pathname);
};
